/**
 * L'arbre de compétences (SPEC-UI §3.5, tâche M2.25) — ⟦A-5 étage 5⟧ : il n'existe qu'à
 * partir du premier niveau de chaîne gagné, et il vit dans la chambre sous la forme d'un
 * **panneau de liège** (⟦A-7⟧ décision 5) qui ouvre ce panneau-ci.
 *
 * Rien n'est décidé ici : le prix d'un nœud, ses prérequis et la raison exacte d'un refus
 * viennent tous du moteur (`coutPcDuNoeud`, `coutEurosDuNoeud`, `refusAchat`). L'écran
 * affiche, et envoie `acheter_skill`.
 *
 * ⟦A-1 point 5⟧ La part en Clicks des nœuds de tier ≤ 2 est **annulée** avant le Grand Saut
 * (« la débrouille ») : `coutEurosDuNoeud` le sait, on montre juste que c'est offert.
 *
 * ⟦A-83 · SPEC-UI §3.5 · A-39 §6⟧ **L'écran dit enfin ce qu'un nœud rapporte.** Jusqu'ici le
 * joueur achetait sur une description littéraire ; il lit désormais l'effet chiffré, l'assist
 * d'Atelier (icône du mini-jeu assisté + chiffre) et les contre-mesures d'événements couvertes
 * — tout vient de `lecture.ts`, qui LIT le contenu sans re-dériver une règle. L'achat passe par
 * un **dialogue maison récapitulatif** (§3.5 « clic → dialogue → `buy_skill` »), jamais par
 * `window.confirm`. Le respec en avait déjà un.
 *
 * ⟦C62 · lot arbre · A-179⟧ **Simple et limpide.** Matt, 21/10/2026. L'Arbre montrait 42 nœuds d'un coup à un joueur
 * qui arrive à 100 abonnés avec 2 points, en jargon (« +0,05 de potentiel Qualité », un identifiant interne
 * `reporter_evenement`), avec 51 boutons « Les dessous » (un par nœud). Désormais :
 *
 * - **un nœud par branche au départ**, le suivant se dévoile quand le sien est pris (`progressif.ts`, réglage
 *   `meta.arbre_progressif`, défaut éteint = l'Arbre d'avant) ; un nœud pris tient en UNE ligne ;
 * - **une phrase de joueur par carte** (`effet_court` du contenu, sinon le premier effet d'avant), des mots
 *   plutôt que des multiplicateurs (`lecture.ts`), plus de palier `t3`, plus d'identifiant ;
 * - **un seul « ? » pour la fenêtre** : le détail d'un nœud (description, effets, parades) s'ouvre en
 *   CLIQUANT le nœud, dans le même dialogue (`DialogueDessous`). Et il ne redit pas la carte.
 */

import { useRef, useState, type RefObject } from "react";

import {
  SEUIL_RESPEC,
  contenuSkills,
  coutEurosDuNoeud,
  coutPcDuNoeud,
  coutRespec,
  eurosAnnulesParDebrouille,
  evenementsTirables,
  prerequisManquants,
  refusAchat,
  remboursementRespec,
  systemesCoupes,
  type BuyableSkill,
} from "@maxime/shared";

import { DialogueDessous, DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { IconeAsset } from "../../components/IconeAsset";
import { Panneau } from "../../components/Panneau";
import { useSfx } from "../../components/SoundProvider";
import { contenu } from "../../game/content";
import { nombre, points } from "../../game/format";
import { interrupteurs } from "../../game/menage";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

import { lectureDuNoeud, lignesDeContremesures } from "./lecture";
import { effetsSansLaCarte, iconesDAssists, lignesDAssists, texteDeCarte } from "./lecture-court";
import { affichageDeLaBranche } from "./progressif";

/** Les 6 branches et leurs icônes (PLAN §4.12, SPEC-UI §8.1). */
const BRANCHES: { id: string; nom: string; icone: string }[] = [
  { id: "tournage", nom: "Tournage", icone: "🎥" },
  { id: "montage", nom: "Montage", icone: "✂️" },
  { id: "ecriture", nom: "Écriture", icone: "✍️" },
  { id: "charisme", nom: "Charisme", icone: "💬" },
  { id: "business", nom: "Business", icone: "💼" },
  { id: "algorithme", nom: "Algorithme", icone: "📈" },
];

/** Un nœud tel que le contenu le porte : la vue moteur, plus ses textes et ses assists. */
type NoeudContenu = BuyableSkill & {
  nom?: string;
  description?: string;
  /** ⟦C62⟧ Ce que le nœud rapporte, en une phrase de joueur (contenu). */
  effet_court?: string;
  branche?: string;
  assists?: readonly { minigame: string; param: string; value: number }[];
};

export function Arbre() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const sfx = useSfx();
  /** Le nœud dont le détail est ouvert (un clic sur la carte) : UN dialogue pour toute la fenêtre. */
  const [detail, setDetail] = useState<string | null>(null);
  /** Ce qui reprend le focus à la fermeture du détail : le nom du nœud cliqué. */
  const retourDetail = useRef<HTMLElement | null>(null);

  if (!run) return null;
  const state = run.state;
  const c = contenu();
  const skills = contenuSkills(c);
  const respecOuvert = state.subscribers >= SEUIL_RESPEC;
  // ⟦C60 · lot b⟧ Une parade ne cite que des événements qui peuvent encore tomber, et une capacité d'un système
  // coupé n'est plus annoncée : l'Arbre ne parle d'aucun système que le ménage a retiré.
  const evenementsVivants = evenementsTirables(c.events, c);
  const coupes = systemesCoupes(c);
  // ⟦C60 bis · A-177⟧ Les Clicks ne paient plus que le Marché et l'Équipe : aucun nœud n'en coûte, le respec est
  // gratuit. L'écran ne redit un prix en ₵ que si le contenu en remet un (le jeu d'avant, mot pour mot).
  const prixRespec = coutRespec(state, interrupteurs());
  const lesNoeudsCoutentDesClicks = skills.skills.some((s) => s.cout_euros > 0);
  // ⟦C62 · lot arbre⟧ Se dévoile-t-il marche par marche ? (`meta.arbre_progressif`, défaut : l'Arbre d'avant.)
  const progressif = c.meta.arbre_progressif;

  const nomDe = (id: string): string =>
    (skills.skills.find((s) => s.id === id) as NoeudContenu | undefined)?.nom ?? id;

  /** La lecture d'un nœud : contenu → phrases. Rien n'est calculé, tout est relu. */
  const lire = (noeud: NoeudContenu) =>
    lectureDuNoeud(noeud as Parameters<typeof lectureDuNoeud>[0], evenementsVivants, coupes);

  /** Ouvre le détail d'un nœud ; le focus reviendra sur son nom à la fermeture. */
  function ouvrirDetail(id: string, depuis: HTMLElement | null) {
    retourDetail.current = depuis;
    setDetail(id);
  }

  /**
   * §3.5 : « clic → dialogue maison récapitulatif → `buy_skill` ». ⟦C57 · A-174⟧ Le récapitulatif ne
   * redit plus que le prix et ce qu'il restera : ce que le nœud rapporte est sur sa carte (l'effet
   * principal) et dans ses dessous (tout le reste) : on ne le recopie pas une troisième fois.
   */
  async function prendre(noeud: NoeudContenu) {
    const euros = coutEurosDuNoeud(state, noeud);
    const offert = eurosAnnulesParDebrouille(state, noeud);
    const cout =
      points(coutPcDuNoeud(noeud)) +
      (euros > 0 ? ` et ${nombre(euros)} ₵` : "") +
      (offert > 0 ? ` (${nombre(offert)} ₵ offerts : la débrouille)` : "");
    const ok = await dialog.confirm({
      title: `Prendre « ${noeud.nom ?? noeud.id} » ?`,
      body:
        `Ça coûte ${cout}. Il te restera ${points(state.pc - coutPcDuNoeud(noeud))}` +
        (euros > 0 ? ` et ${nombre(state.money - euros)} ₵` : "") +
        ".",
      confirmLabel: "Prendre",
      cancelLabel: "Pas maintenant",
    });
    if (!ok) return;
    // ⟦SPEC-UI §7.6⟧ `skill_unlock` sonne quand le MOTEUR a accepté — jamais au clic.
    const fait = await act({ type: "acheter_skill", skill_id: noeud.id });
    if (fait) sfx("skill_unlock");
  }

  async function respec() {
    // ⟦C60 bis · A-177⟧ Le prix de base du respec est un réglage du contenu (`systemes.respec_clicks`) : à 0, il est
    // GRATUIT (les Clicks ne paient plus que le Marché et l'Équipe), et la boîte ne parle ni de ₵ ni de « double ».
    const prix = prixRespec;
    const rendu = remboursementRespec(state, skills);
    const ok = await dialog.confirm({
      title: "Tout remettre à zéro ?",
      body:
        prix > 0
          ? `Tu récupères ${points(rendu)} pour ${nombre(prix)} ₵. Le prochain respec coûtera le double.`
          : `Tu récupères ${points(rendu)}, gratuitement. Tu pourras tout reprendre autrement.`,
      confirmLabel: "Repartir de zéro",
      cancelLabel: "Garder ma patte",
      danger: true,
    });
    if (!ok) return;
    const fait = await act({ type: "respec" });
    if (fait) sfx("respec");
  }

  /**
   * ⟦C62⟧ Pourquoi la carte est grisée, écrit sous elle (une raison grisée sans mot est une impasse) : la porte
   * d'abonnés, ce qu'il faut prendre d'abord, ou les points qui manquent. `null` quand on peut prendre.
   */
  function raisonDuRefus(noeud: NoeudContenu): string | null {
    const manquants = prerequisManquants(state, noeud);
    if (manquants.length > 0) return `Il te faut d'abord : ${manquants.map(nomDe).join(", ")}`;
    const refus = refusAchat(state, noeud.id, skills);
    if (!refus) return null;
    if (refus.code === "skill_locked_subs") return `À partir de ${nombre(noeud.abonnes_min ?? 0)} abonnés`;
    if (refus.code === "not_enough_pc") return `Il te manque ${points(coutPcDuNoeud(noeud) - state.pc)}`;
    return null;
  }

  const noeudDuDetail = detail ? (skills.skills.find((s) => s.id === detail) as NoeudContenu | undefined) : undefined;

  return (
    <Panneau titre={`🌳 Arbre de compétences : ${points(state.pc)}`} large>
      {/* ⟦C57 · A-174⟧ Une phrase courte ; le mode d'emploi entier est dans « Les dessous ». ⟦C62⟧ Le SEUL « ? » de la
          fenêtre : il explique l'Arbre, et ne redit pas la phrase d'à côté. Le détail d'un nœud s'ouvre sur le nœud. */}
      <p className="ligne-info discret">
        Ta patte : personne ne prend tout l'arbre.{" "}
        <LesDessous titre="Lire l'arbre">
          <DessousBloc>
            {progressif ? (
              <p className="dessous-texte">
                Chaque branche se dévoile marche par marche : prends une compétence, la suivante apparaît.
              </p>
            ) : null}
            <p className="dessous-texte">
              Appuie sur une compétence pour lire tout ce qu'elle rapporte, et les événements qu'elle amortit.
            </p>
            <p className="dessous-texte">
              Tes points viennent de ta progression : niveaux, paliers d'abonnés et grands événements.
            </p>
            <p className="dessous-texte">
              {`Passé ${nombre(SEUIL_RESPEC)} abonnés, tu peux tout remettre à zéro et récupérer tes points${
                prixRespec > 0 ? ` contre ${nombre(prixRespec)} ₵` : ", gratuitement"
              }.`}
            </p>
            {lesNoeudsCoutentDesClicks ? (
              <p className="dessous-texte">
                Avant le Grand Saut, « la débrouille » t'offre la part en Clicks des premières compétences.
              </p>
            ) : null}
          </DessousBloc>
        </LesDessous>
      </p>

      {/* Le respec s'ouvre à 10 000 abonnés (PLAN §4.12) : avant, un choix est un choix. */}
      {respecOuvert && state.skills.length > 0 ? (
        <p className="ligne-info">
          <button className="lien" type="button" disabled={pending} onClick={() => void respec()}>
            ↺ Tout remettre à zéro
          </button>{" "}
          · {prixRespec > 0 ? `${nombre(prixRespec)} ₵ · ` : ""}+{points(remboursementRespec(state, skills))}
        </p>
      ) : null}

      <div className="branches">
        {BRANCHES.map((branche) => {
          const deLaBranche = skills.skills.filter(
            (s) => (s as NoeudContenu).branche === branche.id,
          ) as NoeudContenu[];
          if (deLaBranche.length === 0) return null;
          const { acquis, suivants } = affichageDeLaBranche(deLaBranche, state.skills, progressif);
          // Une branche dont plus rien ne reste à écrire (tout pris, ou le reste hors de portée) garde ses acquis.
          return (
            <section className="carte branche" key={branche.id}>
              <h2 className="bloc-titre">
                <span aria-hidden="true">{branche.icone}</span> {branche.nom}
              </h2>
              <ul className="noeuds">
                {/* Ce qui est pris : une ligne, le nom ouvre le détail. */}
                {acquis.map((noeud) => (
                  <li className="noeud possede noeud-acquis-ligne" key={noeud.id}>
                    <button
                      type="button"
                      className="noeud-acquis"
                      aria-haspopup="dialog"
                      onClick={(e) => ouvrirDetail(noeud.id, e.currentTarget)}
                    >
                      <IconeAsset className="noeud-icone" src={`/materiel/skill-${noeud.id}.png`} />
                      <span className="noeud-nom">{noeud.nom ?? noeud.id}</span>
                      <span className="noeud-etat">acquis ✔</span>
                    </button>
                  </li>
                ))}
                {/* Ce qu'on peut prendre ensuite : une carte, cliquable pour le détail. */}
                {suivants.map((noeud) => {
                  const lecture = lire(noeud);
                  const carte = texteDeCarte(noeud, lecture);
                  const iconesAssist = iconesDAssists(lecture);
                  const euros = coutEurosDuNoeud(state, noeud);
                  const raison = raisonDuRefus(noeud);
                  const refuse = refusAchat(state, noeud.id, skills) !== null;
                  const nom = noeud.nom ?? noeud.id;
                  return (
                    <li
                      className={`noeud noeud-carte ${refuse ? "refuse" : "achetable"}`}
                      key={noeud.id}
                      onClick={(e) => {
                        // Le bouton « Prendre » et le nom (un bouton) gardent leur propre clic.
                        if ((e.target as HTMLElement).closest("button")) return;
                        ouvrirDetail(noeud.id, e.currentTarget.querySelector<HTMLElement>(".noeud-nom-bouton"));
                      }}
                    >
                      <div className="noeud-tete">
                        {/* ⟦C15 · A-135 · A-136⟧ 39 des 42 nœuds ont une icône produite
                            (`assets/img-src/materiel/skill-<id>.png`) : les 3 restants n'en ont pas encore,
                            `IconeAsset` replie sur rien du tout. */}
                        <IconeAsset className="noeud-icone" src={`/materiel/skill-${noeud.id}.png`} />
                        <button
                          type="button"
                          className="noeud-nom noeud-nom-bouton"
                          aria-haspopup="dialog"
                          onClick={(e) => ouvrirDetail(noeud.id, e.currentTarget)}
                        >
                          {nom}
                        </button>
                      </div>
                      {carte ? <p className="noeud-effet">{carte}</p> : null}
                      <div className="noeud-pied">
                        <span className="noeud-cout">
                          {points(coutPcDuNoeud(noeud))}
                          {euros > 0 ? ` · ${nombre(euros)} ₵` : ""}
                        </span>
                        {iconesAssist.length > 0 ? (
                          <span className="noeud-assist" aria-label="Mini-jeux assistés">
                            {iconesAssist.map((icone) => (
                              <span key={icone} aria-hidden="true">
                                {icone}
                              </span>
                            ))}
                          </span>
                        ) : null}
                        <button
                          className="bouton"
                          type="button"
                          disabled={pending || refuse}
                          onClick={() => void prendre(noeud)}
                        >
                          Prendre
                        </button>
                      </div>
                      {raison ? <p className="noeud-refus">🔒 {raison}</p> : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      {/* Le détail d'UN nœud : le dialogue « Les dessous » commandé d'ici, sans « ? » par ligne. */}
      {noeudDuDetail ? (
        <DetailDuNoeud
          noeud={noeudDuDetail}
          lecture={lire(noeudDuDetail)}
          retour={retourDetail}
          onFermer={() => setDetail(null)}
        />
      ) : null}
    </Panneau>
  );
}

/**
 * ⟦C62 · lot arbre⟧ **Le détail d'un nœud** : ce que la carte ne dit pas. La description littéraire, les effets
 * chiffrés (sans la ligne que la carte écrit déjà), les mini-jeux qu'il assiste, les événements qu'il amortit.
 * Ni le prix, ni la porte, ni le prérequis : la carte les écrit, le détail ne les redit pas.
 */
function DetailDuNoeud({
  noeud,
  lecture,
  retour,
  onFermer,
}: {
  noeud: NoeudContenu;
  lecture: ReturnType<typeof lectureDuNoeud>;
  retour: RefObject<HTMLElement | null>;
  onFermer(): void;
}) {
  const carte = texteDeCarte(noeud, lecture);
  const rapporte = [
    ...effetsSansLaCarte(lecture, carte).map((texte) => ({ icone: "▸", texte })),
    ...lignesDAssists(lecture),
  ];
  const parades = lignesDeContremesures(lecture);
  return (
    <DialogueDessous titre={noeud.nom ?? noeud.id} onFermer={onFermer} retour={retour}>
      {noeud.description ? (
        <DessousBloc>
          <p className="dessous-texte">{noeud.description}</p>
        </DessousBloc>
      ) : null}
      {rapporte.length > 0 ? (
        <DessousBloc titre="Ce que ça rapporte">
          <DessousLignes lignes={rapporte} />
        </DessousBloc>
      ) : null}
      {parades.length > 0 ? (
        <DessousBloc titre="Les événements qu'il amortit">
          <DessousLignes lignes={parades.map((texte) => ({ icone: "🛡", texte }))} />
        </DessousBloc>
      ) : null}
    </DialogueDessous>
  );
}
